import React, { useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, XIcon } from 'lucide-react';
import { Density, FormConfig } from '../../types';
import { fieldCatalog, moveItem } from '../../utils/config';
import { METRIC_ACCENTS } from '../../utils/metrics';
import { Segmented, Select, Toggle } from './AdminControls';
interface LayoutTabProps {
  config: FormConfig;
  onChange: (config: FormConfig) => void;
}
function sortedFieldOptions() {
  return Object.values(fieldCatalog).map(f => ({
    value: f.id,
    label: f.label
  })).sort((a, b) => a.label.localeCompare(b.label));
}
function labelFor(id: string): string {
  return fieldCatalog[id]?.label ?? id;
}
interface FieldPickerProps {
  ids: string[];
  options: {
    value: string;
    label: string;
  }[];
  max: number;
  /** Renders a small accent chip in front of each row */
  accents?: boolean;
  emptyText: string;
  onChange: (ids: string[]) => void;
}
function FieldPicker({
  ids,
  options: allOptions,
  max,
  accents = false,
  emptyText,
  onChange
}: FieldPickerProps) {
  const options = allOptions.filter(o => !ids.includes(o.value));
  return <div data-id="element-28" data-mp="1auznrql85kio">
      <ul className="mb-2 space-y-1.5" data-id="element-29" data-mp="1au6r5ifzcbe9">
        {ids.length === 0 && <li className="rounded-lg border border-dashed border-slate-300 py-4 text-center text-xs text-slate-400" data-id="element-30" data-mp="15vwqwtfeekls">
            {emptyText}
          </li>}
        {ids.map((id, index) => <li key={id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5" data-id="element-31" data-mp="1ylltvcbo4uq1" data-mpi={index}>
            <div className="flex flex-col" data-id="element-32" data-mp="1tkg33k1kjhj5">
              <button type="button" aria-label={`Move ${labelFor(id)} up`} disabled={index === 0} onClick={() => onChange(moveItem(ids, index, -1))} className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-33" data-mp="1omegwq8uj23g">
                <ChevronUpIcon className="h-3 w-3" data-id="element-34" data-mp="954bzz47ayll" />
              </button>
              <button type="button" aria-label={`Move ${labelFor(id)} down`} disabled={index === ids.length - 1} onClick={() => onChange(moveItem(ids, index, 1))} className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-35" data-mp="aomhygroyftg">
                <ChevronDownIcon className="h-3 w-3" data-id="element-36" data-mp="5qb6k1lqzx94" />
              </button>
            </div>

            {accents && <span className={`h-4 w-1.5 shrink-0 rounded-full ${METRIC_ACCENTS[index % METRIC_ACCENTS.length].bar}`} aria-hidden="true" data-id="element-37" data-mp="3ssxvr2v3fg9e" />}

            <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700" data-id="element-38" data-mp="3j46651di981n">
              {labelFor(id)}
            </span>

            <span className="shrink-0 text-[10px] font-bold tabular-nums text-slate-300" data-id="element-39" data-mp="355zgg8ipmed2">
              {index + 1}
            </span>

            <button type="button" aria-label={`Remove ${labelFor(id)}`} onClick={() => onChange(ids.filter(x => x !== id))} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-40" data-mp="2qg4smtnnl3c0">
              <XIcon className="h-3.5 w-3.5" data-id="element-41" data-mp="2ugyk1x0b109n" />
            </button>
          </li>)}
      </ul>

      {ids.length < max ? <Select value="" ariaLabel="Add an item" onChange={v => v && onChange([...ids, v])} options={[{
      value: '',
      label: `Add an item… (${ids.length}/${max})`
    }, ...options]} data-id="element-42" data-mp="34gpczu97v77m" /> : <p className="text-[11px] text-slate-400" data-id="element-43" data-mp="3e7jyuxijw4sj">
          Maximum of {max} reached — remove one to add another.
        </p>}
    </div>;
}
export function LayoutTab({
  config,
  onChange
}: LayoutTabProps) {
  const fieldOptions = useMemo(sortedFieldOptions, []);
  return <div className="space-y-6" data-id="element-44" data-mp="b4fdvj94z0xl">
      <section data-id="element-45" data-mp="b3e4dj7rqmhz">
        <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-id="element-46" data-mp="b1ta7upd95kn">
          Density
        </h3>
        <p className="mb-2 text-xs text-slate-500" data-id="element-47" data-mp="jw7l7l2v5txy">
          Controls padding and spacing across every field on the record.
        </p>
        <Segmented<Density> ariaLabel="Form density" value={config.density} options={[{
        value: 'comfortable',
        label: 'Comfortable'
      }, {
        value: 'compact',
        label: 'Compact'
      }]} onChange={v => onChange({
        ...config,
        density: v
      })} data-id="element-48" data-mp="ou94a60aht9f" />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white px-3 py-1" data-id="element-49" data-mp="14eujxb9okte">
        <Toggle label="Show record header" description="The borrower banner with record type and workflow status." checked={config.showHeader} onChange={v => onChange({
        ...config,
        showHeader: v
      })} data-id="element-50" data-mp="65m5244sny3r" />
        <div className="border-t border-slate-100" data-id="element-51" data-mp="321qx9tqovvbo">
          <Toggle label="Show field search" description="Lets users filter the record by field name." checked={config.showSearch} onChange={v => onChange({
          ...config,
          showSearch: v
        })} data-id="element-52" data-mp="321h7a65n9o5z" />
        </div>
        <div className="border-t border-slate-100" data-id="element-53" data-mp="2ndns2mkh0nnx">
          <Toggle label="Show completion rail" description="Per-section progress tracker pinned to the right of the record." checked={config.showChecklist} onChange={v => onChange({
          ...config,
          showChecklist: v
        })} data-id="element-54" data-mp="2ncui7cq7ti1k" />
        </div>
      </section>

      <section data-id="element-55" data-mp="29ffmxrda6g1o">
        <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-id="element-56" data-mp="24e9snp3z2kq1">
          Summary tiles
        </h3>
        <p className="mb-3 text-xs text-slate-500" data-id="element-57" data-mp="7m87mhxo3yfs">
          Pick up to 5 fields to display as accent tiles pinned to the top of the record. Order here
          sets both position and tile color.
        </p>
        <FieldPicker ids={config.headerMetricIds} options={fieldOptions} max={5} accents emptyText="No tiles — the top of the record will start with the header." onChange={ids => onChange({
        ...config,
        headerMetricIds: ids
      })} data-id="element-58" data-mp="1x22fy5heflo7" />
      </section>
    </div>;
}